import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.round(n * 1000) / 1000) : String(v ?? '');
};

/**
 * Jawaban nakes untuk satu pertanyaan barang keluar (Cek Barang Keluar).
 * Ya: jumlah menurut sistem benar. Tidak: barang tidak keluar. Jumlah lain: nakes mengetik angka yang benar.
 * Dipakai di kartu Dashboard dan di pop up.
 */
export default function CbkAnswerControls(props) {
  // Pertanyaan bebas dari SPV dijawab dengan teks; pertanyaan barang dijawab Ya, Tidak, atau jumlah.
  return props.q.line_type === 'pertanyaan_bebas' ? <FreeTextAnswer {...props} /> : <NumericAnswer {...props} />;
}

function FreeTextAnswer({ q, onDone, testPrefix = 'cbk' }) {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const send = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cek-barang-keluar/kasus/${q.kasus_id}/jawaban-teks`, { text: text.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Jawaban terkirim ke SPV');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal mengirim jawaban');
    } finally {
      setBusy(false);
      if (onDone) onDone();
    }
  };
  return (
    <div className="mt-2 space-y-2">
      <textarea
        value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1000}
        placeholder="Tulis jawabanmu"
        className="w-full border rounded-md px-2 py-1.5 text-sm"
        data-testid={`${testPrefix}-teks-${q.kasus_id}`}
      />
      <Button
        size="sm" disabled={busy || text.trim().length === 0} onClick={send}
        className="w-full" data-testid={`${testPrefix}-kirim-teks-${q.kasus_id}`}
      >
        Kirim jawaban
      </Button>
    </div>
  );
}

function NumericAnswer({ q, onDone, testPrefix = 'cbk' }) {
  const [busy, setBusy] = useState(false);
  const [other, setOther] = useState(false);
  const [val, setVal] = useState('');
  // Barang yang hanya ada di laporan nakes: patokannya jumlah di laporan nakes sendiri.
  const fromReport = q.line_type === 'hanya_di_laporan_nakes';
  const refQty = fromReport ? q.app_qty : q.accurate_qty;
  const sys = fmt(refQty);
  // Selisih jumlah, barang sudah ada di laporan nakes: "Tidak keluar" (0) menyesatkan karena akan menghapus
  // barang dari laporan. Gantinya "Tidak, tetap N": laporan tidak diubah, admin menyesuaikan sistem.
  const keepReport = q.line_type === 'qty_beda' && Number(q.app_qty) > 0;
  const keepQty = fmt(q.app_qty);

  const send = async (answerIn, qty) => {
    let answer = answerIn;
    let msg;
    if (answer === 'jumlah' && Number(qty) === Number(refQty)) answer = 'ya';
    if (keepReport && answer === 'jumlah' && Number(qty) === Number(q.app_qty)) {
      msg = `Yakin yang keluar tetap ${keepQty} pcs sesuai laporanmu? Laporanmu tidak diubah dan admin diminta menyesuaikan sistem.`;
    } else if (fromReport) {
      if (answer === 'ya') msg = `Yakin barang ini keluar ${sys} pcs? Laporanmu tidak diubah dan admin diminta menambahkannya di sistem.`;
      else if (answer === 'tidak' || Number(qty) === 0) msg = 'Yakin barang ini tidak keluar sama sekali? Barang ini akan dihapus dari laporan pengeluaranmu dan tidak bisa dibatalkan.';
      else msg = `Yakin jumlah yang keluar ${fmt(qty)} pcs? Laporan pengeluaranmu akan diubah dan admin diminta menyesuaikan sistem.`;
    } else if (answer === 'ya') msg = `Yakin barang ini keluar ${sys} pcs? Laporan pengeluaranmu akan disesuaikan dan tidak bisa dibatalkan.`;
    else if (answer === 'tidak') msg = 'Yakin barang ini tidak keluar sama sekali? Laporan pengeluaranmu akan diubah dan admin diminta mengoreksi stok.';
    else msg = `Yakin jumlah yang keluar ${fmt(qty)} pcs? Laporan pengeluaranmu akan diubah dan admin diminta mengoreksi stok.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const body = answer === 'jumlah' ? { answer, qty: Number(qty) } : { answer };
      await axios.post(`${API}/cek-barang-keluar/kasus/${q.kasus_id}/jawaban-nakes`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(keepReport && answer === 'jumlah' && Number(qty) === Number(q.app_qty)
        ? 'Jawaban tersimpan, admin diminta menyesuaikan sistem'
        : 'Jawaban tersimpan, laporan pengeluaran disesuaikan');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan jawaban');
    } finally {
      setBusy(false);
      if (onDone) onDone();
    }
  };

  const valid = val !== '' && Number.isFinite(Number(val)) && Number(val) >= 0;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-2">
        <Button
          size="sm" disabled={busy} onClick={() => send('ya')}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
          data-testid={`${testPrefix}-ya-${q.kasus_id}`}
        >
          {`Ya, keluar ${sys}`}
        </Button>
        {keepReport ? (
          <Button
            size="sm" variant="outline" disabled={busy} onClick={() => send('jumlah', q.app_qty)}
            className="flex-1" data-testid={`${testPrefix}-tetap-${q.kasus_id}`}
          >
            {`Tidak, tetap ${keepQty}`}
          </Button>
        ) : (
          <Button
            size="sm" variant="outline" disabled={busy} onClick={() => send('tidak')}
            className="flex-1" data-testid={`${testPrefix}-tidak-${q.kasus_id}`}
          >
            Tidak keluar
          </Button>
        )}
      </div>
      {(
        !other ? (
          <button
            type="button" className="text-xs text-purple-800 underline"
            onClick={() => setOther(true)} data-testid={`${testPrefix}-lain-${q.kasus_id}`}
          >
            Jumlahnya lain
          </button>
        ) : (
          <div className="flex gap-2 items-center">
            <input
              type="number" inputMode="decimal" min="0" step="any" value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="Jumlah yang keluar"
              className="flex-1 border rounded-md px-2 py-1 text-sm"
              data-testid={`${testPrefix}-qty-${q.kasus_id}`}
            />
            <Button
              size="sm" disabled={busy || !valid} onClick={() => send('jumlah', val)}
              data-testid={`${testPrefix}-kirim-${q.kasus_id}`}
            >
              Kirim
            </Button>
          </div>
        )
      )}
    </div>
  );
}
